import { Euler, Vector3 } from "three";
import { MeshGroup, StateMachineType } from "../meshGroup/MeshGroup";
import { createMeshInstances, MeshInstance } from "./MeshInstance";
import { RawModel } from "../rawModel/RawModel";
import { BoundingRect, getCombinedBoundingRect } from "../BoundingRect";
import { getResizedVector3 } from "../resizeVertices";
import { IStateMachine } from "../animation/StateMachine";
import { DoorStateMachine } from "../animation/DoorStateMachine";
import { v4 as uuidv4 } from "uuid";
import { MeshStats } from "../modelConfig/MeshStats";
import { ModelConfig } from "../modelConfig/ModelConfig";

export type GroupInstanceCreationOptions = {
    groupId: string;
    targetBoundaries: BoundingRect;
    sourceBoundaries?: "model" | "group" | BoundingRect;
};

export type GroupInstance = {
    groupId: string;
    instanceId: string;
    config: MeshGroup;
    meshes: Record<string, MeshInstance>;
    position: Vector3;
    rotation: Euler;
    meshPositions: Record<string, Vector3>;
    meshRotations: Record<string, Euler>;
    stateMachine?: IStateMachine;
};

export const createGroupInstances = (
    config: ModelConfig,
    meshStatsProvider: (meshName: string) => MeshStats,
    model: RawModel,
    targetBoundaries: BoundingRect,
    skipUvResize?: boolean,
    groupInstanceCreationOptions?: GroupInstanceCreationOptions[]
) => {
    const groupInstances = Array<GroupInstance>();

    if (groupInstanceCreationOptions) {
        for (const options of groupInstanceCreationOptions) {
            const group = config.meshGroups[options.groupId];
            const sourceBoundaries =
                options.sourceBoundaries !== undefined &&
                typeof options.sourceBoundaries === "object"
                    ? options.sourceBoundaries
                    : options.sourceBoundaries === "model"
                    ? model.boundingRect
                    : getGroupBoundingRect(group, model);

            const groupInstance = createGroupInstance(
                group,
                config,
                meshStatsProvider,
                model,
                sourceBoundaries,
                options.targetBoundaries,
                skipUvResize
            );

            groupInstances.push(groupInstance);
        }

        return groupInstances;
    }

    for (const group of Object.values(config.meshGroups)) {
        const groupInstance = createGroupInstance(
            group,
            config,
            meshStatsProvider,
            model,
            model.boundingRect,
            targetBoundaries,
            skipUvResize
        );

        groupInstances.push(groupInstance);
    }

    return groupInstances;
};

export const createGroupInstance = (
    group: MeshGroup,
    config: ModelConfig,
    meshStatsProvider: (meshName: string) => MeshStats,
    model: RawModel,
    sourceBoundaries: BoundingRect,
    targetBoundaries: BoundingRect,
    skipUvResize?: boolean
): GroupInstance => {
    const position = getResizedVector3(
        group.position,
        model.boundingRect.center,
        model.boundingRect,
        targetBoundaries
    );

    const delta = new Vector3().subVectors(group.position, position);

    const meshInstances = createGroupMeshInstances(
        group,
        model,
        config,
        meshStatsProvider,
        sourceBoundaries,
        targetBoundaries,
        skipUvResize
    );

    const newMeshPositions: Record<string, Vector3> = {};
    const newMeshRotations: Record<string, Euler> = {};

    for (const meshName of group.meshNames) {
        const newPosition = new Vector3().addVectors(group.meshPositions[meshName], delta);

        newMeshPositions[meshName] = newPosition;
        newMeshRotations[meshName] = new Euler(
            group.meshRotations[meshName].x,
            group.meshRotations[meshName].y,
            group.meshRotations[meshName].z
        );
    }

    const instance: GroupInstance = {
        groupId: group.id,
        instanceId: uuidv4(),
        config: group,
        meshes: meshInstances,
        position,
        rotation: new Euler(group.rotation.x, group.rotation.y, group.rotation.z),
        meshPositions: newMeshPositions,
        meshRotations: newMeshRotations,
    };

    if (group.stateMachineType) {
        instance.stateMachine = createStateMachine(instance, group.stateMachineType);
    }

    return instance;
};

const DOOR_OPEN_ANGLE = Math.PI / 2 - Math.PI / 16;
const DOOR_CLOSED_ANGLE = 0;

const createStateMachine = (
    group: GroupInstance,
    stateMachineType: StateMachineType
): IStateMachine | undefined => {
    switch (stateMachineType) {
        case "leftDoor":
            return new DoorStateMachine(group, DOOR_OPEN_ANGLE, DOOR_CLOSED_ANGLE);
        case "rightDoor":
            return new DoorStateMachine(group, -DOOR_OPEN_ANGLE, -DOOR_CLOSED_ANGLE);
        default:
            return undefined;
    }
};

const createGroupMeshInstances = (
    group: MeshGroup,
    model: RawModel,
    config: ModelConfig,
    meshStatsProvider: (meshName: string) => MeshStats,
    sourceBoundaries: BoundingRect,
    targetBoundaries: BoundingRect,
    skipUvResize?: boolean
) => {
    const groupMeshes = getGroupMeshes(group, model);

    const meshInstances = createMeshInstances(
        groupMeshes,
        config,
        meshStatsProvider,
        sourceBoundaries,
        targetBoundaries,
        skipUvResize
    );

    return meshInstances;
};

const getGroupBoundingRect = (group: MeshGroup, model: RawModel) => {
    const meshes = getGroupMeshes(group, model);

    const boundingRects = meshes.map((mesh) => mesh.boundingRect);

    return getCombinedBoundingRect(boundingRects);
};

const getGroupMeshes = (group: MeshGroup, model: RawModel) => {
    return Object.values(model.meshes).filter((mesh) => group.meshNames.includes(mesh.name));
};
